import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay, switchMap, filter, tap, catchError } from 'rxjs/operators';
import { EupHttpHandler } from '@core/eupHttpHandler.service';
import { HttpParams } from '@angular/common/http';
import { EupRoutesService } from '@core/eupRoutes.service';
import { ViewerSoftwareOptions, SnapshotSoftwareOptions } from '@shared/enums';
import { ViewerSoftwareOptionsResponse, SnapshotResponse } from '@shared/generalInterfaces';
import { IKeyValuePair } from '@interfaces/IKeyValuePair';
import { CompanySoftwareOptionsStore } from '../state/cswo-store';
import { CompanySoftwareOptionsQuery } from '../state/cswo-query';
import { ShellContextService } from 'app/services/shell-context/shell-context.service';

@Injectable({
	providedIn: 'root',
})
export class SoftwareOptionsService {
	private companySoftwareOptions$: Observable<IKeyValuePair> = this.query.companySoftwareOptions$;

	constructor(
		private http: EupHttpHandler,
		private eupRoutesService: EupRoutesService,
		private store: CompanySoftwareOptionsStore,
		private query: CompanySoftwareOptionsQuery,
		private shellContextService: ShellContextService
	) {}

    // #region Deprecated Methods - Use getCompanySWO() instead

	isSoftwareOptionEnabled$(softwareOptionParams: HttpParams): Observable<Object[]> {
		return this.http.get(
			this.eupRoutesService.userSettings.areScannerSoftwareOptionsAvailableUrl,
			{ params: softwareOptionParams, withCredentials: true },
			true,
			false
		);
	}

	hasEvx(companyId): Observable<boolean> {
		return this.getSoftwareOptions(companyId, ViewerSoftwareOptions).pipe(
			map((res) => {
				return !!(res[ViewerSoftwareOptions.ShouldEnableEvx] || res[ViewerSoftwareOptions.NGNIRI]);
			})
		);
	}

	getSoftwareOptionsForViewer(companyId): Observable<ViewerSoftwareOptionsResponse> {
		return this.getSoftwareOptions(companyId, ViewerSoftwareOptions).pipe(
			map((res) => {
				const responseObject = new ViewerSoftwareOptionsResponse();
				responseObject.enableNiriReviewTool = !!res[ViewerSoftwareOptions.ShouldEnableEvx.toString()];
				responseObject.enableIntraoralCameraTool = !!res[ViewerSoftwareOptions.ShouldEnableIntraoralCameraTool.toString()];
				responseObject.enableTextureMappingInViewer = !!res[ViewerSoftwareOptions.ShouldEnableTextureMappingInViewer.toString()];
				return responseObject;
			})
		);
	}

	getSnapshotSoftwareOptions(companyId): Observable<SnapshotResponse> {
		return this.getSoftwareOptions(companyId, SnapshotSoftwareOptions).pipe(
			map((res) => {
				const responseObject = new SnapshotResponse(
					res[SnapshotSoftwareOptions.ShouldDisableCaptureTool.toString()],
					res[SnapshotSoftwareOptions.ShouldDisableDrawingTool.toString()]
				);
				return responseObject;
			})
		);
	}

	private getSoftwareOptions(companyId, softwareOptions) {
		let params = new HttpParams();
		for (const softwareOption in softwareOptions) {
			if (!isNaN(Number(softwareOption))) {
				params = params.append('softwareOptions', softwareOption);
			}
		}
		params = params.append('companyId', companyId);
		return this.isSoftwareOptionEnabled$(params);
	}

	isSoftwareOptionExists(softwareOptionId: number, softwareOptions: number[]): boolean {
		return softwareOptions && softwareOptions.some((so) => so === softwareOptionId);
	}

	areSoftwareOptionExist(softwareOptionIds: number[], softwareOptions: number[]): boolean {
		return softwareOptions && softwareOptions.some((so) => softwareOptionIds.includes(so));
	}

	areAllSoftwareOptionsExists(softwareOptionIds: number[], softwareOptions: number[]): boolean {
		return softwareOptions && softwareOptionIds.every((id) => softwareOptions.includes(id));
	}

    /**
     * @deprecated Use getCompanySWO() and array methods instead. This method will be removed in future versions.
     */
	getCompanySoftwareOptions(selectedCompanyId): Observable<number[]> {
		return this.http.get(this.eupRoutesService.userSettings.getCompanySoftwareOptionIds + selectedCompanyId).pipe(
			map((res) => {
				return res as number[];
			})
		);
	}
	
    // #endregion

	getCompanySoftwareOptionsKeyValuePair(selectedCompanyId, forceUpdate = false): Observable<IKeyValuePair> {		// Check if data is already in the store
		// Check if data is already in the store
		if ( !forceUpdate && this.query.isCswoUpdated) {
			return this.companySoftwareOptions$;
        }

		// Otherwise, make an HTTP call and update the store
		return this.http.get<IKeyValuePair>(this.eupRoutesService.userSettings.getCompanySoftwareOptions + selectedCompanyId).pipe(
			tap((res) => {
				this.updateCompanySoftwareOptions(res, new Date());
            }),
			shareReplay(1)
		);
	}

	getCompanySoftwareOptionsKeys(selectedCompanyId, forceUpdate = false): Observable<string[]> {
		return this.getCompanySoftwareOptionsKeyValuePair(selectedCompanyId, forceUpdate).pipe(
			map((res) => Object.keys(res))
		);
	}

	updateCompanySoftwareOptions(companySoftwareOptions: IKeyValuePair, date: Date): void {
		this.store.update((state) => {
			state.companySoftwareOptions = companySoftwareOptions;
			state.lastUpdate = date;
		});
	}

	resetCompanySoftwareOptions(): void {
		this.updateCompanySoftwareOptions({}, null);
	}
}
